import { useMemo } from 'react'
import { formatUSD, formatPercent } from '@/lib/format-utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'
import dayjs from 'dayjs'
import { ROASDailyDataTable } from './base-virtualized-table'

const SIMPLIFIED_RANGES = ["D0", "D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8-14", "D15-30", "D31-60", "D61-90", "D91+"]

const getSourceDateText = (revenueDate, range) => {
  const rDate = dayjs(revenueDate)
  
  if (range.includes('-') || range.endsWith('+')) {
    if (range === 'D91+') return `Installed before ${rDate.subtract(91, 'day').format('MM-DD')}`
    const [start, end] = range.substring(1).split('-').map(Number)
    return `${rDate.subtract(end, "day").format("MM-DD")} ~ ${rDate
      .subtract(start, "day")
      .format("MM-DD")}`
  }
  
  const day = parseInt(range.substring(1))
  return rDate.subtract(day, 'day').format('YYYY-MM-DD')
}

const RevenueMixTable = ({ data, displayMode, isSimplified }) => {
  const isPercentage = displayMode === 'percentage'

  const allCohortKeys = useMemo(() => {
    if (isSimplified) {
      const activeSimplified = new Set()
      data.forEach(d => {
        SIMPLIFIED_RANGES.forEach(r => {
          if (d.ranges[r] !== undefined) activeSimplified.add(r)
        })
      })
      return SIMPLIFIED_RANGES.filter(r => activeSimplified.has(r))
    }
    
    const keys = new Set()
    data.forEach(item => {
      Object.keys(item.ranges).forEach(k => keys.add(k))
    })
    return Array.from(keys).sort((a, b) => {
      const aVal = parseInt(a.substring(1))
      const bVal = parseInt(b.substring(1))
      return aVal - bVal
    })
  }, [data, isSimplified])

  const columns = useMemo(() => {
    const baseCols = [
      {
        code: 'display_date',
        title: <span className="text-base text-neutral-950">Revenue Date</span>,
        width: 250,
        lock: true,
        render: (val) => (
          <div className="font-medium text-left px-2 text-base text-neutral-950 whitespace-nowrap">
            {val}
          </div>
        ),
      },
      {
        code: 'total',
        title: <span className="text-base text-neutral-950">Revenue</span>,
        width: 120,
        align: 'right',
        lock: true,
        render: (val) => (
          <div className="font-bold px-2 text-base text-neutral-950">
            {formatUSD(val)}
          </div>
        ),
      },
      {
        code: 'cost',
        title: <span className="text-base text-neutral-950">Cost</span>,
        width: 120,
        align: 'right',
        lock: true,
        render: (val) => (
          <div className="font-medium px-2 text-base text-neutral-950">
            {formatUSD(val)}
          </div>
        ),
      },
      {
        code: 'roas',
        title: <span className="text-base text-neutral-950">ROAS</span>,
        width: 120,
        align: 'right',
        lock: true,
        render: (val) => (
          <div className="font-medium px-2 text-base text-neutral-950">
            {typeof val === 'number' ? val.toFixed(2) + '%' : '-'}
          </div>
        ),
      },
      {
        code: 'profit',
        title: <span className="text-base text-neutral-950">Profit</span>,
        width: 120,
        align: 'right',
        lock: true,
        render: (val) => (
          <div className={`font-medium px-2 text-base ${val >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatUSD(val)}
          </div>
        ),
      },
      {
        code: 'er',
        title: <span className="text-base text-neutral-950">ER</span>,
        width: 120,
        align: 'right',
        lock: true,
        render: (val) => (
          <div className="font-medium px-2 text-base text-neutral-950">
            {typeof val === 'number' ? `${val.toFixed(2)}%` : '-'}
          </div>
        ),
      },
    ]

    const cohortCols = allCohortKeys.map((range) => ({
      code: range,
      title: <span className="text-base text-neutral-950">{range}</span>,
      width: 120,
      align: 'right',
      render: (_, row) => {
        const val = row.ranges[range] || 0
        if (val <= 0) return <div className="px-2 text-muted-foreground text-base">-</div>

        const pct = row.total > 0 ? val / row.total : 0
        const sourceDateText = getSourceDateText(row.revenue_date, range)

        const content = isPercentage ? (
          <div className="font-medium w-full text-right px-2 text-base text-neutral-950">
            {formatPercent(pct)}
          </div>
        ) : (
          <div className="flex flex-col items-end w-full px-2 text-neutral-950">
            <span className="text-base font-medium">{formatUSD(val)}</span>
            <span className="text-[10px] text-muted-foreground">
              {formatPercent(pct)}
            </span>
          </div>
        )

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              {content}
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-xs">
                <p>Cohort: <span className="font-bold">{range}</span></p>
                <p>Source: <span className="font-bold">{sourceDateText}</span></p>
                <p>Revenue: <span className="font-bold">{formatUSD(val)}</span></p>
                <p>Share: <span className="font-bold">{formatPercent(pct)}</span></p>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      },
    }))

    return [...baseCols, ...cohortCols]
  }, [isPercentage, allCohortKeys])

  return (
    <TooltipProvider delayDuration={100}>
      <div className="rounded-md border mt-2" style={{ height: '600px' }}>
        <ROASDailyDataTable
          dataSource={data}
          columns={columns}
          primaryKey="rowId"
        />
      </div>
    </TooltipProvider>
  )
}

export default RevenueMixTable
